import { Linking } from 'react-native'
import moment from 'moment'
import * as AddCalendarEvent from 'react-native-add-calendar-event'

import Config from 'APP/Config'
import Alert from 'APP/Converse/Alert'
import { createIcsFile } from 'APP/Helpers/IcsUtils'
import { isIOS, isWeb } from 'APP/Helpers/checkPlatform'
import { useShareDeepLinkFromConfig } from 'APP/Hooks/Share'
import { useSelector } from 'APP/Hooks/redux'
import I18n from 'APP/I18n'
import { getBrandConfig } from 'APP/Lib/ConfigHelpers'
import { selectPreferredLanguage } from 'APP/Redux/PatientRedux'

interface AddToCalendarParams {
  startAt: string
  endAt: string
  episodeId?: string | null
  isVirtual: boolean
  analyticsName: string
}

/**
 * Builds the calendar event for an appointment and hands it to the OS picker, falling back to a
 * downloadable `.ics` file on web. Returns the press handler.
 */
export const useAddToCalendar = ({
  startAt,
  endAt,
  episodeId,
  isVirtual,
  analyticsName,
}: AddToCalendarParams) => {
  // TODO [DIA-89203]: investigate what it would take to add the Calendar deeplink to Android. It is currently only supported on iOS
  const enableCalendarDeeplink = isIOS()
  const preferredLanguage = useSelector(selectPreferredLanguage)

  const episodeLink = useShareDeepLinkFromConfig(
    'ADD_TO_CALENDAR_EPISODE_DEEP_LINK_URL',
    { chat: episodeId },
    { analyticsName: `${analyticsName}.episodeLink` }
  )

  return () => {
    // TODO: add handling logic for unsupported locales in getBrandConfig instead of casting here
    const { productName } = getBrandConfig(Config.BRAND_ID, preferredLanguage)
    const titleKey = isVirtual ? 'Appointments.calendarTitle' : 'Appointments.calendarTitleInPerson'
    const title = I18n.t(titleKey, { productName, lng: preferredLanguage })
    const cancellationPolicy = I18n.t('Appointments.calendarCancellationPolicy', {
      lng: preferredLanguage,
    })
    const description = enableCalendarDeeplink
      ? `${I18n.t('Appointments.calendarJoinLink', {
          episodeLink: episodeLink.url,
          lng: preferredLanguage,
        })}${cancellationPolicy}`
      : cancellationPolicy
    const startDate = moment.utc(startAt)
    const endDate = moment.utc(endAt)

    const onError = () => {
      Alert.alert(I18n.t('CannotAddToCalendar.title'), I18n.t('CannotAddToCalendar.body'), [
        { text: I18n.t('CannotAddToCalendar.ok') },
      ])
    }

    if (isWeb()) {
      createIcsFile(title, description, startDate, endDate)
        .then((result) => Linking.openURL(result))
        .catch(onError)
    } else {
      const eventConfig = {
        title,
        startDate: startDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        endDate: endDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        notes: description,
      }

      AddCalendarEvent.presentEventCreatingDialog(eventConfig).catch(onError)
    }
  }
}
